/**
 * API Client
 * 
 * Centralized HTTP request handler with automatic authentication, error handling,
 * token refresh on 401 responses, and request timeout management. Implements
 * single-flight token refresh to prevent concurrent refresh requests.
 */

import { API_CONFIG } from '@/app/config/apiConfig.js';
import logger from '@/shared/utils/logger.js';
import { buildUrl, buildRefreshUrl } from './requestUtils';
import { tokenService } from '@/features/auth/services/tokenService.js';

const API_BASE_URL = API_CONFIG.AUTH_BASE_URL;
const DISBURSEMENT_BASE_URL = API_CONFIG.DISBURSEMENT_BASE_URL;
const COLLECTION_BASE_URL = API_CONFIG.COLLECTION_BASE_URL;
const UTILITY_BASE_URL = API_CONFIG.UTILITY_BASE_URL;
const REFRESH_ENDPOINT = API_CONFIG.AUTH_REFRESH_ENDPOINT || '/auth/token/refresh';

let refreshingPromise = null;

// Request deduplication: Track in-flight requests to prevent duplicate calls
const pendingRequests = new Map();

/**
 * Creates a unique key for a request based on endpoint and options
 */
const getRequestKey = (endpoint, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const body = options.body ? (typeof options.body === 'string' ? options.body : JSON.stringify(options.body)) : '';
  const headers = options.headers ? JSON.stringify(options.headers) : '';
  return `${method}:${endpoint}:${body}:${headers}`;
};

/**
 * Determines the appropriate base URL based on the endpoint path.
 * Disbursement-related endpoints use DISBURSEMENT_BASE_URL, others use AUTH_BASE_URL.
 * @param {string} endpoint - The API endpoint path
 * @param {string} [explicitBaseUrl] - Explicitly provided base URL (takes precedence)
 * @returns {string} The base URL to use for the request
 */
const getBaseUrlForEndpoint = (endpoint, explicitBaseUrl) => {
  // If explicit baseUrl is provided, use it
  if (explicitBaseUrl) {
    return explicitBaseUrl;
  }

  // Check if this is a disbursement-related endpoint
  const disbursementPatterns = [
    '/v1/disbursements',
    '/disbursements',
    '/v1/settlements',
    '/v1/employee-payments',
  ];

  const isDisbursementEndpoint = disbursementPatterns.some(pattern =>
    endpoint.includes(pattern)
  );

  if (isDisbursementEndpoint && DISBURSEMENT_BASE_URL) {
    return DISBURSEMENT_BASE_URL;
  }

  // Check if this is a collection/invoice-related endpoint
  // Only /invoice_collection, /momo-collection, and /bank-collection should use the collection service
  const collectionPatterns = [
    '/invoice_collection',
    '/momo-collection',
    '/bank-collection',
    '/ismartpaycheckout',
  ];

  const isCollectionEndpoint = collectionPatterns.some(pattern =>
    endpoint.includes(pattern)
  );

  if (isCollectionEndpoint && COLLECTION_BASE_URL) {
    return COLLECTION_BASE_URL;
  }

  // Check if this is a utility-related endpoint
  const utilityPatterns = [
    '/utility',
    '/buy-',
  ];

  const isUtilityEndpoint = utilityPatterns.some(pattern =>
    endpoint.includes(pattern)
  );

  if (isUtilityEndpoint && UTILITY_BASE_URL) {
    return UTILITY_BASE_URL;
  }

  // Default to AUTH_BASE_URL
  return API_BASE_URL;
};

/**
 * Centralized fetch wrapper that normalizes URLs, injects authentication headers,
 * handles non-2xx responses with friendly error messages, and implements
 * single-flight token refresh with automatic retry on 401 responses.
 * Also implements request deduplication to prevent duplicate simultaneous requests.
 */
export const apiRequest = async (endpoint, options = {}) => {
  // Check if logout is in progress - if so, reject all API calls EXCEPT the logout API itself
  const isLoggingOut = typeof localStorage !== 'undefined' && localStorage.getItem('isLoggingOut') === 'true';
  const isLogoutEndpoint = endpoint.includes('/logout');

  // Allow login and auth endpoints even during logout to prevent getting stuck
  const isAuthEndpoint = endpoint.includes('/login') || endpoint.includes('/publicauth/') || endpoint.includes('/token/refresh');

  if (isLoggingOut && !isLogoutEndpoint && !isAuthEndpoint) {
    logger.warn('[apiClient] Logout in progress, rejecting API call', { endpoint });
    throw new Error('Logout in progress. API calls are disabled.');
  }

  // Request deduplication: Check if an identical request is already in flight
  // Skip deduplication for mutations (POST, PUT, DELETE, PATCH) to allow intentional duplicates
  const method = (options.method || 'GET').toUpperCase();
  const isMutation = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);
  const requestKey = getRequestKey(endpoint, options);

  if (!isMutation && pendingRequests.has(requestKey)) {
    logger.debug('[apiClient] Deduplicating request', { endpoint, requestKey });
    // Return the existing promise for this request
    return pendingRequests.get(requestKey);
  }

  const { baseUrl, ...restOptionsInput } = options || {};
  const requestBaseUrl = getBaseUrlForEndpoint(endpoint, baseUrl);
  const url = buildUrl(requestBaseUrl, endpoint);

  logger.debug('[apiClient] Final request URL:', { url });

  // Create the request promise
  const requestPromise = (async () => {
    // Capture variables for cleanup
    const shouldDeduplicate = !isMutation;
    const dedupKey = requestKey;

    const isPublicEndpoint = endpoint.includes('/publicauth/') ||
      endpoint.includes('/public/') ||
      (options.headers && options.headers['X-Skip-Auth'] === '1');

    const authHeader = {};
    let storedToken = null;
    try {
      storedToken = (typeof localStorage !== 'undefined') ? localStorage.getItem('authToken') : null;
      if (storedToken && !isPublicEndpoint) {
        authHeader.Authorization = `Bearer ${storedToken}`;
      }
    } catch (e) {
    }

    const { headers: _optionsHeaders, ...restOptions } = restOptionsInput;

    const contentTypeHeader = (options.body instanceof FormData)
      ? {}
      : { 'Content-Type': 'application/json' };

    const defaultOptions = {
      headers: {
        'Accept': 'application/json',
        ...contentTypeHeader,
        ...authHeader,
        ...(_optionsHeaders || {}),
      },
      ...restOptions,
    };

    logger.debug('[apiClient] Request headers prepared', defaultOptions.headers);
    logger.debug('API Request', {
      url: url,
      method: options.method || 'GET',
      hasAuth: !!storedToken && !isPublicEndpoint,
      endpoint: endpoint,
      isPublic: isPublicEndpoint,
      tokenLength: storedToken ? storedToken.length : 0,
      tokenPreview: storedToken ? `${storedToken.substring(0, 20)}...${storedToken.substring(storedToken.length - 10)}` : 'none',
    });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(url, {
        ...defaultOptions,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const storedToken = (typeof localStorage !== 'undefined') ? localStorage.getItem('authToken') : null;
        let tokenAudience = 'unknown';
        if (storedToken) {
          try {
            const parts = storedToken.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              tokenAudience = payload.aud || 'no-aud';
            }
          } catch (e) {
          }
        }
        logger.warn('API Error Response detected', {
          status: response.status,
          statusText: response.statusText,
          url: url,
          ok: response.ok,
          tokenAudience: tokenAudience,
          hasToken: !!storedToken
        });
        let errorMessage = 'Request failed.';
        let rawErrorData = null;
        let errorData = {}; // Declare outside try block so it's accessible everywhere

        try {
          const responseText = await response.text();
          rawErrorData = responseText;

          // Check if this is the logout endpoint or team endpoint to prevent infinite loops
          const isLogoutEndpoint = endpoint.includes('/logout');
          const isTeamEndpoint = endpoint.includes('/team/');

          // Check for "Missing token" in raw response text before parsing
          // Skip auto-logout for logout and team endpoints to prevent infinite loops
          if (responseText && (responseText.includes('"Missing token"') || responseText.includes('Missing token'))) {
            if (isLogoutEndpoint || isTeamEndpoint) {
              // For logout and team endpoints, just throw the error without auto-logout to prevent loops
              logger.warn(`Missing token on ${isLogoutEndpoint ? 'logout' : 'team'} endpoint - skipping auto-logout to prevent loop`);
              throw new Error('Session expired. Please log in again.');
            }
            logger.warn('Missing token detected in raw response - logging out user automatically');
            try {
              if (typeof localStorage !== 'undefined') {
                localStorage.setItem('isLoggingOut', 'true');
              }
              tokenService.safeLogout();
              // Redirect to login page after a short delay to allow cleanup
              if (typeof window !== 'undefined') {
                setTimeout(() => {
                  window.location.href = '/login';
                }, 100);
              }
            } catch (logoutError) {
              logger.error('Error during automatic logout', logoutError);
            }
            throw new Error('Session expired. Please log in again.');
          }

          if (response.status === 413) {
            errorMessage = 'File size too large. Please upload a file smaller than 5MB.';
            const sizeError = new Error(errorMessage);
            sizeError.status = 413;
            sizeError.statusText = response.statusText;
            throw sizeError;
          }
          if (responseText) {
            try {
              errorData = JSON.parse(responseText);
            } catch (parseError) {
              if (responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')) {
                if (response.status === 413) {
                  errorMessage = 'File size too large. Please upload a file smaller than 5MB.';
                } else {
                  errorMessage = `Server error (${response.status}): ${response.statusText}`;
                }
                const htmlError = new Error(errorMessage);
                htmlError.status = response.status;
                htmlError.statusText = response.statusText;
                throw htmlError;
              }
              errorMessage = responseText.substring(0, 200);
            }
          }
          const message = errorData.message || errorData.error || '';

          logger.warn('API Error Response', {
            status: response.status,
            statusText: response.statusText,
            body: responseText
          });

          // Handle "Missing token" error by automatically logging out
          // Skip auto-logout for logout and team endpoints to prevent infinite loops
          if (message === 'Missing token' || message === 'Missing token.' || message?.toLowerCase().includes('missing token')) {
            if (isLogoutEndpoint || isTeamEndpoint) {
              // For logout and team endpoints, just throw the error without auto-logout to prevent loops
              logger.warn(`Missing token on ${isLogoutEndpoint ? 'logout' : 'team'} endpoint - skipping auto-logout to prevent loop`);
              throw new Error('Session expired. Please log in again.');
            }
            logger.warn('Missing token detected - logging out user automatically');
            try {
              if (typeof localStorage !== 'undefined') {
                localStorage.setItem('isLoggingOut', 'true');
              }
              tokenService.safeLogout();
              // Redirect to login page after a short delay to allow cleanup
              if (typeof window !== 'undefined') {
                setTimeout(() => {
                  window.location.href = '/login';
                }, 100);
              }
            } catch (logoutError) {
              logger.error('Error during automatic logout', logoutError);
            }
            throw new Error('Session expired. Please log in again.');
          }

          if (response.status === 500 && message === 'Invalid credentials') {
            errorMessage = 'Invalid email or password.';
          } else if (response.status === 500 && message && message.includes('Invalid credentials')) {
            const attemptsMatch = message.match(/(\d+) attempts left/);
            if (attemptsMatch) {
              const attemptsLeft = parseInt(attemptsMatch[1]);
              errorMessage = `Invalid email or password. ${attemptsLeft} attempts remaining.`;
            } else {
              errorMessage = 'Invalid email or password.';
            }
          } else if (response.status === 401) {
            if (errorData && errorData.code === '03' && errorData.message) {
              const apiMessage = errorData.message;
              if (apiMessage.includes('Invalid credentials')) {
                const attemptsMatch = apiMessage.match(/(\d+) attempts/);
                if (attemptsMatch) {
                  const attemptsLeft = parseInt(attemptsMatch[1]);
                  errorMessage = `Invalid email or password. ${attemptsLeft} attempts remaining.`;
                } else {
                  errorMessage = 'Invalid email or password.';
                }
              } else {
                errorMessage = apiMessage;
              }
            } else {
              errorMessage = 'Invalid email or password.';
            }
          } else if (response.status === 400) {
            if (message && message.includes('prisma.refreshToken.delete')) {
              const refreshTokenError = new Error(message);
              refreshTokenError.isRefreshTokenDeleteError = true;
              refreshTokenError.code = errorData?.code || '07';
              throw refreshTokenError;
            }
            errorMessage = 'Invalid login credentials. Please check your email and password.';
          } else if (response.status === 429) {
            errorMessage = 'Too many login attempts. Please try again later.';
          } else if (response.status >= 500) {
            if (message && message.includes('Invalid credentials')) {
              const attemptsMatch = message.match(/(\d+) attempts left/);
              if (attemptsMatch) {
                const attemptsLeft = parseInt(attemptsMatch[1]);
                errorMessage = `Invalid email or password. ${attemptsLeft} attempts remaining.`;
              } else {
                errorMessage = 'Invalid email or password.';
              }
            } else if (message && message.includes('already exists')) {
              errorMessage = 'An account with this email or phone number already exists. Please use a different email address or phone number.';
            } else if (message && message.includes('duplicate')) {
              errorMessage = 'This information is already registered. Please use different details.';
            } else if (message && message.includes('locked') && message.includes('failed login attempts')) {
              errorMessage = message;
            } else if (message && message.includes('prisma.refreshToken.delete')) {
              const refreshTokenError = new Error(message);
              refreshTokenError.isRefreshTokenDeleteError = true;
              refreshTokenError.code = errorData?.code || '07';
              throw refreshTokenError;
            } else {
              logger.error('Server Error Details', {
                status: response.status,
                statusText: response.statusText,
                url: url,
                rawErrorData: rawErrorData,
                parsedMessage: message
              });
              errorMessage = `Server error: ${message || 'Please try again later.'}`;
            }
          } else if (message) {
            errorMessage = message;
          }
        } catch (parseError) {
          if (parseError.status === 413 || response.status === 413) {
            throw parseError;
          }

          logger.error('Failed to parse error response', parseError);
          logger.debug('Raw error response', rawErrorData);

          // Try to parse errorData from rawErrorData in catch block
          if (rawErrorData) {
            try {
              errorData = JSON.parse(rawErrorData);
            } catch (e) {
              // If parsing fails, errorData remains {}
              errorData = {};
            }
          }

          if (response.status === 401) {
            try {
              const fallbackErrorData = rawErrorData ? JSON.parse(rawErrorData) : {};
              errorData = fallbackErrorData; // Update errorData with parsed fallback
              if (fallbackErrorData && fallbackErrorData.code === '03' && fallbackErrorData.message) {
                const apiMessage = fallbackErrorData.message;
                if (apiMessage.includes('Invalid credentials')) {
                  const attemptsMatch = apiMessage.match(/(\d+) attempts/);
                  if (attemptsMatch) {
                    const attemptsLeft = parseInt(attemptsMatch[1]);
                    errorMessage = `Invalid email or password. ${attemptsLeft} attempts remaining.`;
                  } else {
                    errorMessage = 'Invalid email or password.';
                  }
                } else {
                  errorMessage = apiMessage;
                }
              } else {
                errorMessage = 'Invalid email or password.';
              }
            } catch (fallbackParseError) {
              errorMessage = 'Invalid email or password.';
            }
          } else if (response.status === 413) {
            errorMessage = 'File size too large. Please upload a file smaller than 5MB.';
          } else if (response.status === 429) {
            errorMessage = 'Too many login attempts. Please try again later.';
          } else if (response.status >= 500) {
            if (rawErrorData && rawErrorData.includes('Invalid credentials')) {
              const attemptsMatch = rawErrorData.match(/(\d+) attempts left/);
              if (attemptsMatch) {
                const attemptsLeft = parseInt(attemptsMatch[1]);
                errorMessage = `Invalid email or password. ${attemptsLeft} attempts remaining.`;
              } else {
                errorMessage = 'Invalid email or password.';
              }
            } else if (rawErrorData && rawErrorData.includes('already exists')) {
              errorMessage = 'An account with this email or phone number already exists. Please use a different email address or phone number.';
            } else if (rawErrorData && rawErrorData.includes('duplicate')) {
              errorMessage = 'This information is already registered. Please use different details.';
            } else if (rawErrorData && rawErrorData.includes('locked') && rawErrorData.includes('failed login attempts')) {
              errorMessage = 'Your account has been locked due to multiple failed login attempts. Please contact support to unlock.';
            } else {
              logger.error('Server Error Details (fallback)', {
                status: response.status,
                statusText: response.statusText,
                url: url,
                rawErrorData: rawErrorData
              });
              errorMessage = `Server error: ${rawErrorData || 'Please try again later.'}`;
            }
          } else {
            errorMessage = `Error ${response.status}: ${response.statusText}`;
          }
        }

        if (response.status === 401) {
          const isPublicAuthEndpoint = endpoint.includes('/publicauth/login') ||
            endpoint.includes('/publicauth/register') ||
            endpoint.includes('/publicauth/password/') ||
            endpoint.includes('/publicauth/verify-2fa') ||
            endpoint.includes('/publicauth/verify-email') ||
            endpoint.includes('/publicauth/profile/') ||
            endpoint.includes('/publicauth/get-verification-level') ||
            endpoint.includes('/auth/get-verification-level') ||
            endpoint.includes('/auth/profile/verify-pin') ||
            (defaultOptions.headers && defaultOptions.headers['X-Skip-Refresh'] === '1');

          if (isPublicAuthEndpoint) {
            logger.warn('401 on public auth endpoint, not attempting refresh', { endpoint });
            throw new Error(errorMessage);
          }

          try {
            if (!refreshingPromise) {
              const refreshToken = (typeof localStorage !== 'undefined') ? localStorage.getItem('refreshToken') : null;
              if (refreshToken) {
                refreshingPromise = (async () => {
                  const refreshUrl = buildRefreshUrl(API_BASE_URL, REFRESH_ENDPOINT);
                  logger.debug('[apiClient] Attempting token refresh', { refreshUrl });
                  logger.debug('Token Refresh Request', {
                    url: refreshUrl,
                    hasRefreshToken: !!refreshToken,
                    refreshTokenLength: refreshToken ? refreshToken.length : 0,
                  });
                  const r = await fetch(refreshUrl, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Accept': 'application/json',
                      'Authorization': `Bearer ${refreshToken}`
                    }
                  });

                  if (!r.ok) {
                    const errorText = await r.text();
                    logger.warn('Refresh token call failed', { status: r.status, body: errorText, url: refreshUrl });
                    logger.error('Token Refresh Failed', { status: r.status, body: errorText, url: refreshUrl });
                    throw new Error('Refresh failed');
                  }

                  const raw = await r.text();
                  logger.debug('[apiClient] Refresh response', { raw });
                  logger.info('Token Refresh Success', { hasResponse: !!raw });
                  const data = raw ? JSON.parse(raw) : {};

                  // Safely extract tokens with proper null checking
                  const tokens = data?.data?.tokens || data?.tokens || {};
                  const newToken = tokens?.accessToken || data?.accessToken || data?.token;
                  const newRefresh = tokens?.refreshToken || data?.refreshToken || null;
                  if (newToken) {
                    localStorage.setItem('authToken', newToken);
                    logger.debug('[apiClient] New access token stored');
                  }
                  if (newRefresh) {
                    localStorage.setItem('refreshToken', newRefresh);
                    logger.debug('[apiClient] New refresh token stored');
                  }
                  return true;
                })();
              } else {
                throw new Error('Missing refresh token');
              }
            }

            await refreshingPromise;
            refreshingPromise = null;

            const retriedToken = (typeof localStorage !== 'undefined') ? localStorage.getItem('authToken') : null;
            if (retriedToken) {
              defaultOptions.headers.Authorization = `Bearer ${retriedToken}`;
            }

            const retryController = new AbortController();
            const retryTimeoutId = setTimeout(() => retryController.abort(), 30000);

            const retryResp = await fetch(url, {
              ...defaultOptions,
              signal: retryController.signal
            });

            clearTimeout(retryTimeoutId);
            if (!retryResp.ok) {
              const rtText = await retryResp.text();
              logger.warn('Retry API Error Response', { status: retryResp.status, body: rtText });
            } else {
              const rawRetry = await retryResp.text();
              try {
                return rawRetry ? JSON.parse(rawRetry) : {};
              } catch (_e) {
                return {};
              }
            }
          } catch (refreshErr) {
            try {
              if (typeof localStorage !== 'undefined') {
                localStorage.setItem('isLoggingOut', 'true');
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('isAuthenticated');
              }
            } catch (e) { }
            logger.error('Token refresh failed', refreshErr);
            throw new Error('Authentication required');
          }
        }
        logger.warn('Throwing API error', { message: errorMessage, errorCode: errorData?.code, status: response.status });

        // Create error object that preserves error code and response data
        const apiError = new Error(errorMessage);

        // Preserve error code for proper error handling in components
        if (errorData?.code) {
          apiError.code = errorData.code;
        }

        // Preserve full response structure for error handling
        apiError.response = {
          status: response.status,
          statusText: response.statusText,
          data: errorData || {}
        };

        // Also attach data directly for easier access
        if (errorData) {
          apiError.data = errorData;
        }

        throw apiError;
      }

      const raw = await response.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch (_parseErr) {
        data = {};
      }

      logger.debug('API Success Response', {
        status: response.status,
        statusText: response.statusText,
        url: url,
        ok: response.ok,
        hasData: !!data && Object.keys(data).length > 0
      });

      return data;
    } catch (err) {
      // Enhanced error handling with offline detection
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        throw new Error('You appear to be offline. Check your internet connection.');
      }

      if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the server. Please try again in a moment.');
      } else if (err.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }

      throw err;
    } finally {
      // Clean up request deduplication cache after request completes
      if (shouldDeduplicate && pendingRequests.has(dedupKey)) {
        pendingRequests.delete(dedupKey);
      }
    }
  })();

  // Store the promise for deduplication (only for GET requests)
  if (!isMutation) {
    pendingRequests.set(requestKey, requestPromise);
  }

  return requestPromise;
};

export default apiRequest;
